<template>
  <div class="table-wrapper">
    <table>
      <caption class="visually-hidden">
        {{
          $t('VIRTUAL_ASSISTANT_CONSUMPTIONS_REPORT.HEADER')
        }}
      </caption>
      <thead>
        <tr>
          <th>{{ $t('VIRTUAL_ASSISTANT_CONSUMPTIONS_REPORT.TABLE.INBOX') }}</th>
          <th>
            {{
              $t('VIRTUAL_ASSISTANT_CONSUMPTIONS_REPORT.TABLE.TOTAL_SESSIONS')
            }}
          </th>
          <th>
            {{
              $t('VIRTUAL_ASSISTANT_CONSUMPTIONS_REPORT.TABLE.AVG_INTERACTIONS')
            }}
          </th>
          <th>
            {{ $t('VIRTUAL_ASSISTANT_CONSUMPTIONS_REPORT.TABLE.AVG_DURATION') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in paginatedData"
          :key="index"
          :class="{ 'striped-row': index % 2 !== 0 }"
        >
          <td>{{ item.inbox_name || '-' }}</td>
          <td>{{ item.total_sessions }}</td>
          <td>{{ item.avg_interactions.toFixed(2) }}</td>
          <td>{{ item.avg_duration_formatted }}</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
        {{ $t('VIRTUAL_ASSISTANT_CONSUMPTIONS_REPORT.PAGINATION.PREVIOUS') }}
      </button>
      <span>
        {{ $t('VIRTUAL_ASSISTANT_CONSUMPTIONS_REPORT.PAGINATION.PAGE') }}
        {{ currentPage }}
        {{ $t('VIRTUAL_ASSISTANT_CONSUMPTIONS_REPORT.COMMON.OF') }}
        {{ totalPages }}
      </span>
      <button
        @click="goToPage(currentPage + 1)"
        :disabled="currentPage === totalPages"
      >
        {{ $t('VIRTUAL_ASSISTANT_CONSUMPTIONS_REPORT.PAGINATION.NEXT') }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VirtualAssistantEventsReasonsTable',
  props: {
    events: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currentPage: 1,
      perPage: 100,
    };
  },
  computed: {
    groupedData() {
      const groups = {};

      this.events.forEach(event => {
        const inbox = event.inbox_name || '-';

        if (!groups[inbox]) {
          groups[inbox] = {
            inbox_name: inbox,
            total_sessions: 0,
            total_interactions: 0,
            total_duration: 0,
          };
        }

        groups[inbox].total_sessions++;
        groups[inbox].total_interactions += event.interactions_count || 0;
        groups[inbox].total_duration += event.duration_interaction || 0;
      });

      return Object.values(groups)
        .map(group => {
          const avgInteractions =
            group.total_sessions > 0
              ? group.total_interactions / group.total_sessions
              : 0;

          const avgDuration =
            group.total_sessions > 0
              ? group.total_duration / group.total_sessions
              : 0;

          return {
            inbox_name: group.inbox_name,
            total_sessions: group.total_sessions,
            avg_interactions: avgInteractions,
            avg_duration: avgDuration,
            avg_duration_formatted: this.formatDuration(avgDuration),
          };
        })
        .sort((a, b) => b.total_sessions - a.total_sessions);
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.groupedData.slice(start, start + this.perPage);
    },
    totalPages() {
      return Math.ceil(this.groupedData.length / this.perPage);
    },
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    formatDuration(seconds) {
      const totalSeconds = Math.floor(seconds);

      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      return [hours, minutes, secs]
        .map(v => String(v).padStart(2, '0'))
        .join(':');
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  width: 100%;
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
  border: 1px solid #ddd;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

thead {
  background-color: #2563eb;
  color: white;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eaeaea;
  word-break: break-word;
  vertical-align: top;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.striped-row {
  background-color: #f9f9f9;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 16px;
  align-items: center;
}

.pagination button {
  background-color: #2563eb;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
